import './CourseCard.scss'

import { useContext, useEffect, useRef, useState } from 'react'
import { Card } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import parse from 'html-react-parser'
import moment from 'moment'
import cn from 'classnames'

import {
  tryGetCourseField,
  COURSE_FIELD_COURSE_LENGTH,
  isFreeCourse
} from 'components/common/api/Courses'
import {
  getCoursePath,
  ROUTE_COURSES_ALL
} from 'components/common/helpers/Paths'
import { navigateTo } from 'components/common/navigation'
import ProgressBar from 'components/common/progress/ProgressBar'
import { buildRequestURL } from 'components/common/api'
import CourseContext from 'components/common/contexts/CourseContext'
import SubscriptionContext from '../../common/contexts/SubscriptionContext'
import alternativeCourseImage from '../../../assets/images/alternative-course-image.jpeg'

/**
 * Simple Course Card component. Used to display a course title and preview
 */
export default function CourseCard({
  course,
  style,
  observer,
  returnPath = ROUTE_COURSES_ALL,
  className = ''
}) {
  const { progress } = useContext(CourseContext)
  const { userSubscription: subscription, userHasPremiumAccess } =
    useContext(SubscriptionContext)

  const history = useHistory()

  const previewRef = useRef()
  const [courseStepsTotal, setCourseStepsTotal] = useState()
  const [coursePercentComplete, setCoursePercentComplete] = useState()
  const [courseIsLocked, setCourseIsLocked] = useState(true)
  const [courseDuration, setCourseDuration] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  })

  const { hours, minutes } = courseDuration

  const courseLength = tryGetCourseField(course, COURSE_FIELD_COURSE_LENGTH)

  useEffect(() => {
    // Get duration of course sections
    if (course.acf_extended.course_duration) {
      const [hours, minutes, seconds] =
        course.acf_extended.course_duration.split(':')

      setCourseDuration({
        hours: parseInt(hours, 10),
        minutes: parseInt(minutes, 10),
        seconds: parseInt(seconds, 10)
      })
    }
  }, [course])

  /**
   * Helper function to determine whether a course is new this month (e.g., within 60 days)
   */
  const isNewThisMonth = () => moment().diff(course.date_gmt, 'days') <= 60

  /**
   * Helper function to open the course page after clicking on it
   */
  const handleCardClick = course => {
    loadCourse(course.id || course.ID)
  }

  /**
   * Helper function to load the course on user tap/click
   * @param {Number} courseId The ID of the course to load
   */
  const loadCourse = courseId => {
    let url = getCoursePath(courseId)

    // Did we pass a custom returnPath?
    if (returnPath) url = buildRequestURL(url, { returnPath })

    navigateTo(url, history)
  }

  // Subscription hook
  useEffect(() => {
    // Return early if we don't have course or subscription data
    if (!course || !subscription) return
    const isFree = isFreeCourse(course)
    const isLocked = !userHasPremiumAccess && !isFree
    setCourseIsLocked(isLocked)
  }, [course, subscription, userHasPremiumAccess])

  useEffect(() => {
    // Return early of we don't have course or progress data yet
    if (!course || !progress || !observer) return
    const courseProgress = progress.find(match => match.course === course.id)

    let stepsTotal = 0,
      stepsCompleted = 0,
      percentComplete = 0

    // Try to set the value and fail silently if we don't have any progress data
    try {
      stepsTotal = courseProgress.steps_total
      stepsCompleted = courseProgress.steps_completed
      percentComplete = Math.round((stepsCompleted / stepsTotal) * 100)
    } catch (error) {} // Swallow exceptions

    // Add to the observer
    observer.observe(previewRef.current)

    setCourseStepsTotal(stepsTotal)
    setCoursePercentComplete(percentComplete)
  }, [progress, course, observer])

  /**
   * Helper function to get the duration of the course
   * @returns {string} Returns a string with the duration of the course
   */
  const getDurationOfCourse = () => {
    let duration = ''
    if (hours) duration += `${hours} hr `
    if (minutes) duration += `${minutes} min `
    // Don't show seconds on the card
    // if (seconds) duration += `${seconds} sec`
    return duration
  }

  return (
    course && (
      <Card className={cn('my-3 course-card', className)} style={style}>
        {/* Is this a new journey? */}
        {isNewThisMonth() && (
          <span
            className='course-new-banner'
            style={{
              animationDelay:
                Number(style?.animationDelay.replace('s', '')) * 2.5 + 's'
            }}
          >
            New
          </span>
        )}

        {/* Click on the course-preview image and open the course */}
        <div
          className='course-preview'
          // TODO: Add a better compressed version to the API that we can use here
          data-preview-src={
            course.featured_image_url === ''
              ? alternativeCourseImage
              : course.featured_image_url
          }
          ref={previewRef}
          onClick={() => handleCardClick(course)}
        >
          {/* Is this course locked? */}
          {courseIsLocked && (
            <div
              className='course-locked d-flex justify-content-between align-items-center px-2 py-1
            position-absolute bottom-0 end-0 bg-white text-black'
            >
              <i className='bi bi-lock'></i>
              <span>Members only</span>
            </div>
          )}

          {/* Course length banner */}
          <span
            className={cn('course-length-banner', {
              'bg-black': courseLength > 1,
              'd-none': getDurationOfCourse() === '' && courseLength <= 1
            })}
            style={{
              animationDelay:
                Number(style?.animationDelay.replace('s', '')) * 2.5 + 's'
            }}
          >
            {getDurationOfCourse()}

            {courseLength > 1 ? ' Course' : ' Session'}
          </span>
        </div>
        <div
          className={cn(
            'course-content d-flex flex-column justify-content-between py-3 ps-4',
            {
              'bg-primary': courseLength > 1
            }
          )}
        >
          {/* Click on the course title and open the course */}
          <div className='d-flex justify-content-between align-items-start gap-3'>
            <h3
              className='course-title'
              onClick={() => handleCardClick(course)}
            >
              {(course.clean_title && parse(course.clean_title)) ||
                course.post_title}
            </h3>
          </div>
          {/* Don't show progress if we don't have any, open the course if click on course-progress */}{' '}
          {courseIsLocked ? (
            <button
              className='course-locked-button align-self-end px-3'
              onClick={() => handleCardClick(course)}
            >
              Get a sneak peak
            </button>
          ) : (
            <div
              className='course-progress'
              onClick={() => handleCardClick(course)}
            >
              <ProgressBar percent={coursePercentComplete} />
              {coursePercentComplete > 0 ? (
                <div>
                  {coursePercentComplete}% of {courseStepsTotal} sessions
                </div>
              ) : (
                <small>Not started</small>
              )}
            </div>
          )}
        </div>
      </Card>
    )
  )
}
